import type { InfoFinding } from '@/modules/external-monitoring/info-findings/types';
import type { Infraction, Severity } from '@/modules/external-monitoring/infractions/types';

export enum ScanTypeName {
  RemoteAccessPorts = 'RemoteAccessPorts',
  DatabasePorts = 'DatabasePorts',
  WebServicePorts = 'WebServicePorts',
  FileSharingPorts = 'FileSharingPorts',
  EmailPorts = 'EmailPorts',
  InfrastructurePorts = 'InfrastructurePorts',
  SPF = 'SPF',
  DKIM = 'DKIM',
  DMARC = 'DMARC',
  HSTS = 'HSTS',
  CSP = 'CSP',
  SecurityHeaders = 'SecurityHeaders',
  TLS = 'TLS',
  DNS = 'DNS',
}

export type ScanOutcome = {
  infractions: Infraction[];
  infoFindings: InfoFinding[];
  error: string | null;
};

export type AssetScanResult = {
  id: number;
  createdAt: string;
  modifiedAt: string;
  scannedAt: string;
  assetID: string;
  scanTypeID: string;
  scanOutcome: ScanOutcome | null;
  scanPayloadUri: string;
  scanPayload: string;
  assetScanID: string;
};

export type AssetScanTypeNote = {
  id: string;
  organisationID: string;
  // TODO: remove assetID and scanTypeID once migration is completed
  assetID: string;
  scanTypeID: string;
  assetScanID: string;
  note: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  deletedAt: string | null;
  deletedBy: string | null;
};

export type AssetScanNote = {
  id: string;
  organisationID: string;
  assetScanID: string;
  note: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  deletedAt: string | null;
  deletedBy: string | null;
};

export type AssetScanResultWithMetadata = AssetScanResult & {
  scanTypeName: string;
  contextNote: AssetScanTypeNote | null;
  assetScanNote: AssetScanNote | null;
  assetDeletedAt: string | null;
};

export enum FindingType {
  Infraction = 'INFRACTION',
  Informational = 'INFORMATIONAL',
}

export type AssetScanResultFindingBase = {
  scanResultID: number;
  findingID: number;
  severity?: number;
  severityLevel?: Severity;
  type: FindingType;
  code: string;
  scannedAt: string;
  scanType: ScanTypeName;
  assetValue: string;
};

export type AssetScanResultInfoFinding = AssetScanResultFindingBase & {
  type: FindingType.Informational;
} & InfoFinding;

export type AssetScanResultInfractionFinding = AssetScanResultFindingBase & {
  type: FindingType.Infraction;
} & Infraction;

export type AssetScanResultFinding = AssetScanResultInfoFinding | AssetScanResultInfractionFinding;

export enum AssetStatus {
  Active = 'ACTIVE',
  Disabled = 'DISABLED',
  Discovered = 'DISCOVERED',
}

export enum AssetDiscoveryStatus {
  PendingReview = 'PENDING_REVIEW',
  Candidate = 'CANDIDATE',
  Rejected = 'REJECTED',
  Verified = 'VERIFIED',
  Unverified = 'UNVERIFIED',
}

export enum AssetSource {
  System = 'SYSTEM',
  User = 'USER',
}

export type Asset = {
  id: string;
  value: string;
  organisationID: string;
  description: string;
  createdAt: string;
  createdBy: string | null;
  modifiedAt: string;
  modifiedBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  status: AssetStatus;
  source: AssetSource;
  discoveryStatus: AssetDiscoveryStatus;
};
